import React, { useState, useEffect } from 'react';
import { Question, QuestionCreateRequest, QuestionCategory, FieldType } from '../types';
import { adminFormsService } from '../services/admin-forms.service';
import { COLORS } from '../../../../core/constants/colors';
import { getFieldTypeLabel } from '../utils/fieldTypeMapper';

interface QuestionDrawerProps {
  mode: 'view' | 'edit' | 'create';
  question?: Question | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const QuestionDrawer: React.FC<QuestionDrawerProps> = ({
  mode,
  question,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<QuestionCreateRequest>({
    field_id: question?.field_id || '',
    label: question?.label || '',
    description: question?.description || '',
    category: question?.category || 'basic_info',
    field_type: question?.field_type || 'text',
    validation_rules: question?.validation_rules || {},
    scoring_weight: question?.scoring_weight || 0,
    keywords: question?.keywords || [],
    options: question?.options || [],
  });
  const [keywordsInput, setKeywordsInput] = useState(question?.keywords?.join(', ') || '');
  const [optionsInput, setOptionsInput] = useState(question?.options?.join(', ') || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (question) {
        setFormData({
          field_id: question.field_id,
          label: question.label,
          description: question.description || '',
          category: question.category,
          field_type: question.field_type,
          validation_rules: question.validation_rules || {},
          scoring_weight: question.scoring_weight || 0,
          keywords: question.keywords || [],
          options: question.options || [],
        });
        setKeywordsInput(question.keywords?.join(', ') || '');
        setOptionsInput(question.options?.join(', ') || '');
      } else if (mode === 'create') {
        setFormData({
          field_id: '',
          label: '',
          description: '',
          category: 'basic_info',
          field_type: 'text',
          validation_rules: {},
          scoring_weight: 0,
          keywords: [],
          options: [],
        });
        setKeywordsInput('');
        setOptionsInput('');
      }
      setError('');
    }
  }, [isOpen, question, mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const submitData: QuestionCreateRequest = {
        ...formData,
        keywords: keywordsInput.split(',').map(k => k.trim()).filter(k => k.length > 0),
        options: optionsInput.split(',').map(o => o.trim()).filter(o => o.length > 0),
      };

      if (mode === 'edit' && question) {
        const updateData: any = {
          label: submitData.label,
          description: submitData.description,
          category: submitData.category,
          field_type: submitData.field_type,
          validation_rules: submitData.validation_rules,
          scoring_weight: submitData.scoring_weight,
          keywords: submitData.keywords,
          options: submitData.options,
          is_active: question.is_active,
        };
        const result = await adminFormsService.updateQuestion(question.id, updateData);
        if (result) {
          onSuccess();
          onClose();
        } else {
          setError('Failed to update question');
        }
      } else if (mode === 'create') {
        const result = await adminFormsService.createQuestion(submitData);
        if (result) {
          onSuccess();
          onClose();
        } else {
          setError('Failed to create question');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    setFormData({
      field_id: question?.field_id || '',
      label: question?.label || '',
      description: question?.description || '',
      category: question?.category || 'basic_info',
      field_type: question?.field_type || 'text',
      validation_rules: question?.validation_rules || {},
      scoring_weight: question?.scoring_weight || 0,
      keywords: question?.keywords || [],
      options: question?.options || [],
    });
    setKeywordsInput(question?.keywords?.join(', ') || '');
    setOptionsInput(question?.options?.join(', ') || '');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const title = mode === 'create' ? 'Create Question' : mode === 'edit' ? 'Edit Question' : 'Question Details';

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleDiscard}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              {title}
            </h2>
            <button
              onClick={handleDiscard}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {error && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            {mode === 'view' && question ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                    Field ID
                  </label>
                  <p className="text-base font-semibold" style={{ color: '#1F2937' }}>
                    {question.field_id}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                    Label
                  </label>
                  <p className="text-base" style={{ color: '#1F2937' }}>
                    {question.label}
                  </p>
                </div>

                {question.description && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Description
                    </label>
                    <p className="text-base" style={{ color: '#6B7280' }}>
                      {question.description}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Category
                    </label>
                    <p className="text-base font-semibold" style={{ color: '#1F2937' }}>
                      {question.category.replace('_', ' ')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Field Type
                    </label>
                    <p className="text-base font-semibold" style={{ color: '#1F2937' }}>
                      {getFieldTypeLabel(question.field_type)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                    Scoring Weight
                  </label>
                  <p className="text-base" style={{ color: '#1F2937' }}>
                    {question.scoring_weight}
                  </p>
                </div>

                {question.keywords && question.keywords.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Keywords
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {question.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {question.options && question.options.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Options
                    </label>
                    <ul className="list-disc list-inside space-y-1">
                      {question.options.map((option, index) => (
                        <li key={index} className="text-base" style={{ color: '#6B7280' }}>
                          {option}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Status
                    </label>
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium inline-block"
                      style={{
                        backgroundColor: question.is_active ? '#D1FAE5' : '#FEE2E2',
                        color: question.is_active ? '#059669' : '#DC2626',
                      }}
                    >
                      {question.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Created At
                    </label>
                    <p className="text-base" style={{ color: '#6B7280' }}>
                      {new Date(question.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Field ID *
                  </label>
                  <input
                    type="text"
                    value={formData.field_id}
                    onChange={(e) => setFormData({ ...formData, field_id: e.target.value })}
                    required
                    disabled={mode === 'edit'}
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    style={{ borderColor: '#E5E7EB' }}
                    placeholder="e.g., company_name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Label *
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: '#E5E7EB' }}
                    placeholder="Enter question label"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    style={{ borderColor: '#E5E7EB' }}
                    placeholder="Enter question description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as QuestionCategory })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      <option value="basic_info">Basic Info</option>
                      <option value="financial">Financial</option>
                      <option value="operational">Operational</option>
                      <option value="strategic">Strategic</option>
                      <option value="compliance">Compliance</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                      Field Type *
                    </label>
                    <select
                      value={formData.field_type}
                      onChange={(e) => setFormData({ ...formData, field_type: e.target.value as FieldType })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="single-choice">Single Choice</option>
                      <option value="multi-choice">Multi Choice</option>
                      <option value="url">URL</option>
                      <option value="date">Date</option>
                      <option value="boolean">Boolean</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Scoring Weight
                  </label>
                  <input
                    type="number"
                    value={formData.scoring_weight || 0}
                    onChange={(e) => setFormData({ ...formData, scoring_weight: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.1"
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={keywordsInput}
                    onChange={(e) => setKeywordsInput(e.target.value)}
                    placeholder="keyword1, keyword2, keyword3"
                    className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                </div>

                {(formData.field_type === 'single-choice' || formData.field_type === 'multi-choice') && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                      Options (comma-separated) *
                    </label>
                    <input
                      type="text"
                      value={optionsInput}
                      onChange={(e) => setOptionsInput(e.target.value)}
                      placeholder="option1, option2, option3"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                {mode !== 'view' && (
                  <div className="pt-6 space-y-3">
                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: COLORS.admin.primary }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Save changes' : 'Create question'}
                    </button>
                    <button
                      type="button"
                      onClick={handleDiscard}
                      className="w-full py-2 text-sm font-medium hover:underline"
                      style={{ color: '#6B7280' }}
                      disabled={isSubmitting}
                    >
                      Discard changes
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

